import { generatePath } from "react-router-dom";
import { type Case, type Client, DocumentStatus } from "@/briefpoint-client";
import Button from "@/components/Button";
import { Modal } from "@/components/Modals";
import { SHARE_SITES } from "@/lib/constants";
import type { DocumentClient } from "@/lib/types";
import {
  buildShareUrl,
  getDocumentSubTypeShortName,
  isInternetExplorer,
} from "@/lib/utils";
import "./AddToCalendarModal.scss";
import AppleLogo from "@/images/AppleLogo.svg";
import GoogleLogo from "@/images/GoogleLogo.svg";
import OutlookLogo from "@/images/OutlookLogo.svg";

const URL: string = import.meta.env.VITE_BASE_URL;

interface Props {
  isShow: boolean;
  doc: DocumentClient;
  _case?: Case;
  client?: Client;
  onClose: () => void;
}

function CalendarButton({
  onClick,
  text,
  icon,
}: {
  onClick: () => void;
  text: string;
  icon: string;
}) {
  return (
    <Button
      variant="outline-primary"
      onClick={onClick}
      className="calendar-button"
    >
      <div className="button-content-container">
        <img width="14" src={icon} alt={`Icon for ${text}`} />
        <div>{text}</div>
      </div>
    </Button>
  );
}

export default function AddToCalendarModal({
  isShow,
  doc,
  _case,
  client,
  onClose,
}: Props) {
  const rawDocumentUrl = `${URL.split(";")
    .find((x) => !x.includes("bridge"))
    ?.replace(/\/$/, "")}/#${generatePath("/wizard/:id", { id: doc.id })}`;

  const htmlEventDescription = `<strong>Case</strong>: ${_case?.title}<br /><strong>Client</strong>: ${client?.name}<br /><br /><a href="${rawDocumentUrl}">Finish or Export your Response on Briefpoint</a>`;
  const rawEventDescription = `Case: ${_case?.title}\nClient: ${client?.name}\n\nFinish or Export your Response on Briefpoint: ${rawDocumentUrl}`;

  let eventTitle = doc.info?.documentSubType
    ? `${getDocumentSubTypeShortName(doc.info?.documentSubType, doc.info?.jurisdiction ?? "")} Response Due`
    : "";
  if (_case?.title) {
    eventTitle += ` - ${_case.title}`;
  } else if (doc.info?.plaintiffs && doc.info?.defendants) {
    eventTitle += ` - ${doc.info?.plaintiffs} v. ${doc.info?.defendants}`;
  } else if (
    doc?.status &&
    doc.status >= DocumentStatus.InfoConfirmed &&
    doc.info?.caseNumber
  ) {
    eventTitle += ` - Case No. ${doc.info?.caseNumber}`;
  }

  function handleCalendarButtonClick(url: string) {
    const filename = doc.name?.split(".")[0];

    if (url.startsWith("BEGIN")) {
      const blob = new Blob([url], { type: "text/calendar;charset=utf-8" });

      if (isInternetExplorer()) {
        window.navigator.msSaveOrOpenBlob?.(blob, `${filename}.ics`);
      } else {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", `${filename}.ics`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      window.open(url, "_blank");
    }
  }

  return (
    <Modal
      show={isShow}
      onClose={onClose}
      onConfirm={onClose}
      showCancelButton={false}
      showConfirmButton={false}
      title="Add to Your Calendar"
    >
      <div className="button-container">
        <CalendarButton
          text="Apple"
          icon={AppleLogo}
          onClick={() =>
            handleCalendarButtonClick(
              buildShareUrl(
                rawEventDescription,
                doc.info?.dueDateOverridden || doc.info?.dueDate,
                eventTitle,
                SHARE_SITES.ICAL,
                rawDocumentUrl,
              ),
            )
          }
        />
        <CalendarButton
          text="Google Calendar"
          icon={GoogleLogo}
          onClick={() =>
            handleCalendarButtonClick(
              buildShareUrl(
                htmlEventDescription,
                doc.info?.dueDateOverridden || doc.info?.dueDate,
                eventTitle,
                SHARE_SITES.GOOGLE,
              ),
            )
          }
        />
        <CalendarButton
          text="Outlook"
          icon={OutlookLogo}
          onClick={() =>
            handleCalendarButtonClick(
              buildShareUrl(
                rawEventDescription,
                doc.info?.dueDateOverridden || doc.info?.dueDate,
                eventTitle,
                SHARE_SITES.OUTLOOK,
                rawDocumentUrl,
              ),
            )
          }
        />
      </div>
    </Modal>
  );
}
